<script lang="ts">
  import { onMount } from 'svelte';
  import type { ApiClient } from '../lib/api';
  import { formatDate } from '../lib/format';
  import GuardDutyAcknowledgement from '../lib/GuardDutyAcknowledgement.svelte';
  import Loading from '../lib/Loading.svelte';
  import type { Router } from '../lib/router.svelte';
  import SeverityBadge from '../lib/SeverityBadge.svelte';
  import type { GuardDutyFinding, GuardDutyUpdate } from '../lib/types';

  let { api, router, findingId, canManage }: { api: ApiClient; router: Router; findingId: string; canManage: boolean } = $props();
  let summary = $state<GuardDutyFinding | null>(null);
  let events = $state<GuardDutyUpdate[]>([]);
  let cursor = $state<string | null>(null);
  let loading = $state(true);
  let error = $state('');
  let scope = $state<'customer' | 'global'>('customer');
  let severityQualifier = $state(false);
  let accountQualifier = $state(false);
  let resourceQualifier = $state(false);
  let reason = $state('');
  let saving = $state(false);
  let saveMessage = $state('');

  async function load(next = '') {
    try {
      const result = await api.guardDutyFinding(findingId, next);
      summary = result.summary;
      events = next ? [...events, ...result.events] : result.events;
      cursor = result.nextCursor;
      error = '';
    } catch (failure) {
      error = failure instanceof Error ? failure.message : 'GuardDuty finding details could not be loaded.';
    } finally {
      loading = false;
    }
  }

  async function suppress() {
    if (!summary) return;
    saving = true;
    saveMessage = '';
    const qualifiers: Array<'severity' | 'account' | 'resource'> = [];
    if (severityQualifier) qualifiers.push('severity');
    if (accountQualifier) qualifiers.push('account');
    if (resourceQualifier) qualifiers.push('resource');
    try {
      await api.createSuppression({ sourceFindingId: summary.id, scope, qualifiers, reason });
      saveMessage = 'Suppression rule created. Matching retained findings are now hidden from active views.';
      reason = '';
      await load();
    } catch (failure) {
      saveMessage = failure instanceof Error ? failure.message : 'The suppression rule could not be created.';
    } finally {
      saving = false;
    }
  }

  onMount(() => load());
</script>

<button class="back" onclick={() => router.navigate('/guardduty')}>← GuardDuty findings</button>
{#if error}<p class="inline-error" role="alert">{error}</p>{/if}
{#if loading}<Loading />{:else if summary}
  <section class="incident-hero">
    <div><p class="eyebrow">{summary.customerName}</p><h1>{summary.title}</h1><p>{summary.accountName} · {summary.region} · {summary.partition}</p></div>
    <SeverityBadge band={summary.severityBand} severity={summary.severity} />
  </section>
  {#if summary.suppressed}<p class="suppression-notice">Suppressed by {summary.matchedSuppressionRuleIds.length} active {summary.matchedSuppressionRuleIds.length === 1 ? 'rule' : 'rules'}.</p>{/if}
  <dl class="summary-grid guardduty-summary"><div><dt>Finding type</dt><dd><code>{summary.type}</code></dd></div><div><dt>Last updated</dt><dd>{formatDate(summary.updatedAt)}</dd></div><div><dt>Finding count</dt><dd>{summary.count}</dd></div><div><dt>Received updates</dt><dd>{summary.eventCount}</dd></div></dl>
  <section class="detail-section finding-description"><h2>Description</h2><p>{summary.description}</p><dl><div><dt>Finding account</dt><dd><code>{summary.findingAccountId}</code></dd></div><div><dt>Resource</dt><dd>{summary.resource ? `${summary.resource.type}: ${summary.resource.id}` : 'No stable resource identifier'}</dd></div><div><dt>Native finding ID</dt><dd><code>{summary.nativeId}</code></dd></div><div><dt>Finding ARN</dt><dd><code>{summary.arn}</code></dd></div></dl></section>
  <GuardDutyAcknowledgement {api} finding={summary} {canManage} onChanged={() => load()} />
  {#if canManage}
    <section class="detail-section"><h2>Suppress matching findings</h2><p class="muted">Every rule matches this exact finding type. Add qualifiers to narrow it further.</p>
      <form class="suppression-form" onsubmit={(event) => { event.preventDefault(); suppress(); }}>
        <label>Scope<select bind:value={scope}><option value="customer">{summary.customerName} only</option><option value="global">All customers</option></select></label>
        <fieldset><legend>Optional exact qualifiers</legend><label><input type="checkbox" bind:checked={severityQualifier} /> Severity band ({summary.severityBand})</label><label><input type="checkbox" bind:checked={accountQualifier} /> Account ({summary.findingAccountId})</label><label><input type="checkbox" bind:checked={resourceQualifier} disabled={!summary.resource} /> Resource{summary.resource ? ` (${summary.resource.id})` : ' unavailable'}</label></fieldset>
        <label>Reason<textarea bind:value={reason} maxlength="500" required rows="3"></textarea></label>
        <button class="primary" type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create suppression rule'}</button>
      </form>
      {#if saveMessage}<p class="form-message" aria-live="polite">{saveMessage}</p>{/if}
    </section>
  {/if}
  <section class="detail-section"><h2>Update timeline</h2><ol class="timeline">{#each events as event}<li class:stale={event.stale}><div class="timeline-marker" aria-hidden="true"></div><article><header><SeverityBadge band={event.severityBand} severity={event.severity} /><time>{formatDate(event.updatedAt)}</time></header><p>{event.description}</p><small>{event.count} occurrences at this update{event.stale ? ' · received out of order' : ''}{event.suppressionRuleIdsAtReceipt.length ? ' · suppressed when received' : ''}</small></article></li>{/each}</ol>{#if cursor}<button class="secondary" onclick={() => load(cursor ?? '')}>Load more updates</button>{/if}</section>
{/if}
